import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const tabs = [
  { key: "years", label: "Years", icon: "bi-calendar3" },
  { key: "terms", label: "Terms", icon: "bi-calendar-week" },
  { key: "classes", label: "Classes", icon: "bi-layers" },
  { key: "streams", label: "Streams", icon: "bi-diagram-3" },
  { key: "groups", label: "Class Groups", icon: "bi-people" },
  { key: "subjects", label: "Subjects", icon: "bi-book" },
  { key: "classSubjects", label: "Class Subjects", icon: "bi-journal-check" },
  { key: "allocations", label: "Teacher Allocations", icon: "bi-person-workspace" },
];

const emptyForms = {
  year: { name: "", start_date: "", end_date: "", is_active: false },
  term: { academic_year: "", name: "", start_date: "", end_date: "", is_active: false },
  schoolClass: { name: "", level: 1, description: "", is_active: true },
  stream: { name: "", description: "", is_active: true },
  group: {
    academic_year: "",
    school_class: "",
    stream: "",
    class_teacher: "",
    capacity: 35,
    is_active: true,
  },
  subject: {
    code: "",
    name: "",
    subject_type: "CLASS_TEACHER",
    description: "",
    is_core: true,
    is_active: true,
  },
  classSubject: { class_group: "", subject: "", is_compulsory: true, is_active: true },
  allocation: {
    teacher: "",
    class_subject: "",
    term: "",
    is_lead_teacher: false,
    is_active: true,
  },
};

function AcademicSetup() {
  const [activeTab, setActiveTab] = useState("years");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [teachers, setTeachers] = useState([]);
  const [years, setYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classes, setClasses] = useState([]);
  const [streams, setStreams] = useState([]);
  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [forms, setForms] = useState(emptyForms);
  const [editing, setEditing] = useState({});

  const loadAcademicSetup = async () => {
    try {
      setLoading(true);
      const [
        summaryResponse,
        teachersResponse,
        yearsResponse,
        termsResponse,
        classesResponse,
        streamsResponse,
        groupsResponse,
        subjectsResponse,
        classSubjectsResponse,
        allocationsResponse,
      ] = await Promise.all([
        axiosInstance.get("academic/setup/summary/"),
        axiosInstance.get("academic/setup/teachers/"),
        axiosInstance.get("academic/years/"),
        axiosInstance.get("academic/terms/"),
        axiosInstance.get("academic/classes/"),
        axiosInstance.get("academic/streams/"),
        axiosInstance.get("academic/class-groups/"),
        axiosInstance.get("academic/subjects/"),
        axiosInstance.get("academic/class-subjects/"),
        axiosInstance.get("academic/teacher-allocations/"),
      ]);

      setSummary(summaryResponse.data || {});
      setTeachers(teachersResponse.data || []);
      setYears(yearsResponse.data || []);
      setTerms(termsResponse.data || []);
      setClasses(classesResponse.data || []);
      setStreams(streamsResponse.data || []);
      setGroups(groupsResponse.data || []);
      setSubjects(subjectsResponse.data || []);
      setClassSubjects(classSubjectsResponse.data || []);
      setAllocations(allocationsResponse.data || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load academic setup.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAcademicSetup();
  }, []);

  const updateForm = (formName, field, value) => {
    setForms((previous) => ({
      ...previous,
      [formName]: { ...previous[formName], [field]: value },
    }));
  };

  const editNormalizers = {
    year: (record) => ({
      name: record.name || "",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      is_active: Boolean(record.is_active),
    }),
    term: (record) => ({
      academic_year: record.academic_year || "",
      name: record.name || "",
      start_date: record.start_date || "",
      end_date: record.end_date || "",
      is_active: Boolean(record.is_active),
    }),
    schoolClass: (record) => ({
      name: record.name || "",
      level: record.level || 1,
      description: record.description || "",
      is_active: Boolean(record.is_active),
    }),
    stream: (record) => ({
      name: record.name || "",
      description: record.description || "",
      is_active: Boolean(record.is_active),
    }),
    group: (record) => ({
      academic_year: record.academic_year || "",
      school_class: record.school_class || "",
      stream: record.stream || "",
      class_teacher: record.class_teacher || "",
      capacity: record.capacity || 35,
      is_active: Boolean(record.is_active),
    }),
    subject: (record) => ({
      code: record.code || "",
      name: record.name || "",
      subject_type: record.subject_type || "CLASS_TEACHER",
      description: record.description || "",
      is_core: Boolean(record.is_core),
      is_active: Boolean(record.is_active),
    }),
    classSubject: (record) => ({
      class_group: record.class_group || "",
      subject: record.subject || "",
      is_compulsory: Boolean(record.is_compulsory),
      is_active: Boolean(record.is_active),
    }),
    allocation: (record) => ({
      teacher: record.teacher || "",
      class_subject: record.class_subject || "",
      term: record.term || "",
      is_lead_teacher: Boolean(record.is_lead_teacher),
      is_active: Boolean(record.is_active),
    }),
  };

  const startEdit = (formName, record) => {
    setForms((previous) => ({
      ...previous,
      [formName]: editNormalizers[formName](record),
    }));
    setEditing((previous) => ({ ...previous, [formName]: record.id }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = (formName) => {
    setForms((previous) => ({ ...previous, [formName]: emptyForms[formName] }));
    setEditing((previous) => ({ ...previous, [formName]: null }));
  };

  const submitForm = async (event, formName, endpoint, successMessage) => {
    event.preventDefault();

    try {
      setLoading(true);
      const editingId = editing[formName];

      if (editingId) {
        await axiosInstance.patch(`${endpoint}${editingId}/`, forms[formName]);
        toast.success("Record updated.");
      } else {
        await axiosInstance.post(endpoint, forms[formName]);
        toast.success(successMessage);
      }

      setForms((previous) => ({ ...previous, [formName]: emptyForms[formName] }));
      setEditing((previous) => ({ ...previous, [formName]: null }));
      await loadAcademicSetup();
    } catch (error) {
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to save academic setup.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async (endpoint, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;

    try {
      setLoading(true);
      await axiosInstance.delete(endpoint);
      toast.success(`${label} deleted.`);
      await loadAcademicSetup();
    } catch (error) {
      console.error(error);
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = (value) => (
    <span className={`badge ${value ? "bg-success" : "bg-secondary"}`}>
      {value ? "Active" : "Inactive"}
    </span>
  );

  const renderBooleanInput = (formName, field, label) => (
    <label className="form-check form-switch mt-4">
      <input
        className="form-check-input"
        type="checkbox"
        checked={forms[formName][field]}
        onChange={(event) => updateForm(formName, field, event.target.checked)}
      />
      <span className="form-check-label fw-semibold">{label}</span>
    </label>
  );

  const getSubjectTypeBadge = (type, label) => (
    <span className={`badge ${type === "SPECIALIST" ? "bg-info text-dark" : "bg-success"}`}>
      {label || (type === "SPECIALIST" ? "Specialist Subject" : "Class Teacher Subject")}
    </span>
  );

  const classSubjectOptions = classSubjects.map((item) => ({
    value: item.id,
    label: `${item.class_group_name} - ${item.subject_code || ""} ${item.subject_name} (${
      item.subject_type === "SPECIALIST" ? "Specialist" : "Class Teacher"
    })`,
  }));

  const specialistAllocationCount = allocations.filter(
    (item) => item.subject_type === "SPECIALIST" && item.is_active
  ).length;

  const classTeacherAllocationCount = allocations.filter(
    (item) => item.subject_type === "CLASS_TEACHER" && item.is_active
  ).length;

  const renderActions = (formName, record, endpoint, label) => (
    <div className="d-flex align-items-center gap-1">
      <button
        type="button"
        className="btn btn-sm btn-light border"
        title={`Edit ${label}`}
        onClick={() => startEdit(formName, record)}
      >
        <i className="bi bi-pencil-square text-success"></i>
      </button>

      <button
        type="button"
        className="btn btn-sm btn-light border"
        title={`Delete ${label}`}
        onClick={() => deleteRecord(endpoint, label)}
      >
        <i className="bi bi-trash text-danger"></i>
      </button>
    </div>
  );

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Academic Setup</h5>
              <p className="text-muted mb-0">
                Configure years, terms, classes, subjects, and teacher allocations.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-outline-success"
              onClick={loadAcademicSetup}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>

          <div className="row g-2 mt-3">
            {[
              ["Years", summary.academic_years || 0],
              ["Class Groups", summary.class_groups || 0],
              ["Specialist Subjects", summary.specialist_subjects || 0],
              ["Allocations", summary.teacher_allocations || 0],
            ].map(([label, value]) => (
              <div className="col-6 col-xl-3" key={label}>
                <div className="border rounded-3 p-3 bg-light">
                  <small className="text-muted fw-bold">{label}</small>
                  <div className="fs-4 fw-bold" style={{ color: "#125b2d" }}>
                    {value}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${
              activeTab === tab.key ? "btn-success" : "btn-light border"
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "years" && (
        <SetupCard title="Academic Years">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "year", "academic/years/", "Academic year saved.")
            }
          >
            <div className="col-md-4">
              <label className="form-label fw-semibold">Year Name</label>
              <input
                className="form-control"
                placeholder="e.g. 2026"
                value={forms.year.name}
                onChange={(event) => updateForm("year", "name", event.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={forms.year.start_date}
                onChange={(event) => updateForm("year", "start_date", event.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold">End Date</label>
              <input
                type="date"
                className="form-control"
                value={forms.year.end_date}
                onChange={(event) => updateForm("year", "end_date", event.target.value)}
                required
              />
            </div>
            <div className="col-md-2">{renderBooleanInput("year", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Year"
              editing={Boolean(editing.year)}
              onCancel={() => cancelEdit("year")}
            />
          </form>

          <SimpleTable
            headers={["Name", "Start", "End", "Status", "Actions"]}
            rows={years.map((year) => [
              year.name,
              year.start_date,
              year.end_date,
              renderStatus(year.is_active),
              renderActions("year", year, `academic/years/${year.id}/`, year.name),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "terms" && (
        <SetupCard title="Terms">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "term", "academic/terms/", "Term saved.")
            }
          >
            <SelectField
              label="Academic Year"
              value={forms.term.academic_year}
              onChange={(value) => updateForm("term", "academic_year", value)}
              options={years.map((year) => ({ value: year.id, label: year.name }))}
            />
            <div className="col-md-3">
              <label className="form-label fw-semibold">Term Name</label>
              <input
                className="form-control"
                placeholder="e.g. Term 1"
                value={forms.term.name}
                onChange={(event) => updateForm("term", "name", event.target.value)}
                required
              />
            </div>
            <DateField label="Start Date" value={forms.term.start_date} onChange={(value) => updateForm("term", "start_date", value)} />
            <DateField label="End Date" value={forms.term.end_date} onChange={(value) => updateForm("term", "end_date", value)} />
            <div className="col-md-2">{renderBooleanInput("term", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Term"
              editing={Boolean(editing.term)}
              onCancel={() => cancelEdit("term")}
            />
          </form>

          <SimpleTable
            headers={["Year", "Term", "Start", "End", "Status", "Actions"]}
            rows={terms.map((term) => [
              term.academic_year_name,
              term.name,
              term.start_date,
              term.end_date,
              renderStatus(term.is_active),
              renderActions("term", term, `academic/terms/${term.id}/`, term.name),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "classes" && (
        <SetupCard title="Classes / Forms">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "schoolClass", "academic/classes/", "Class saved.")
            }
          >
            <TextField label="Class Name" value={forms.schoolClass.name} onChange={(value) => updateForm("schoolClass", "name", value)} placeholder="e.g. Form 1" />
            <NumberField label="Level" value={forms.schoolClass.level} onChange={(value) => updateForm("schoolClass", "level", value)} />
            <TextField label="Description" value={forms.schoolClass.description} onChange={(value) => updateForm("schoolClass", "description", value)} optional />
            <div className="col-md-2">{renderBooleanInput("schoolClass", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Class"
              editing={Boolean(editing.schoolClass)}
              onCancel={() => cancelEdit("schoolClass")}
            />
          </form>

          <SimpleTable
            headers={["Class", "Level", "Description", "Status", "Actions"]}
            rows={classes.map((schoolClass) => [
              schoolClass.name,
              schoolClass.level,
              schoolClass.description || "-",
              renderStatus(schoolClass.is_active),
              renderActions(
                "schoolClass",
                schoolClass,
                `academic/classes/${schoolClass.id}/`,
                schoolClass.name
              ),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "streams" && (
        <SetupCard title="Streams">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "stream", "academic/streams/", "Stream saved.")
            }
          >
            <TextField label="Stream Name" value={forms.stream.name} onChange={(value) => updateForm("stream", "name", value)} placeholder="e.g. Blue" />
            <TextField label="Description" value={forms.stream.description} onChange={(value) => updateForm("stream", "description", value)} optional />
            <div className="col-md-2">{renderBooleanInput("stream", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Stream"
              editing={Boolean(editing.stream)}
              onCancel={() => cancelEdit("stream")}
            />
          </form>

          <SimpleTable
            headers={["Stream", "Description", "Status", "Actions"]}
            rows={streams.map((stream) => [
              stream.name,
              stream.description || "-",
              renderStatus(stream.is_active),
              renderActions("stream", stream, `academic/streams/${stream.id}/`, stream.name),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "groups" && (
        <SetupCard title="Class Groups">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "group", "academic/class-groups/", "Class group saved.")
            }
          >
            <SelectField label="Academic Year" value={forms.group.academic_year} onChange={(value) => updateForm("group", "academic_year", value)} options={years.map((year) => ({ value: year.id, label: year.name }))} />
            <SelectField label="Class" value={forms.group.school_class} onChange={(value) => updateForm("group", "school_class", value)} options={classes.map((item) => ({ value: item.id, label: item.name }))} />
            <SelectField label="Stream" value={forms.group.stream} onChange={(value) => updateForm("group", "stream", value)} options={streams.map((item) => ({ value: item.id, label: item.name }))} optional />
            <SelectField label="Class Teacher" value={forms.group.class_teacher} onChange={(value) => updateForm("group", "class_teacher", value)} options={teachers.map((teacher) => ({ value: teacher.id, label: `${teacher.full_name} (${teacher.employee_number || "No employee no."})` }))} optional />
            <NumberField label="Capacity" value={forms.group.capacity} onChange={(value) => updateForm("group", "capacity", value)} />
            <div className="col-md-2">{renderBooleanInput("group", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Group"
              editing={Boolean(editing.group)}
              onCancel={() => cancelEdit("group")}
            />
          </form>

          <SimpleTable
            headers={["Year", "Class Group", "Teacher", "Capacity", "Status", "Actions"]}
            rows={groups.map((group) => [
              group.academic_year_name,
              group.display_name,
              group.class_teacher_name || "-",
              group.capacity,
              renderStatus(group.is_active),
              renderActions(
                "group",
                group,
                `academic/class-groups/${group.id}/`,
                group.display_name
              ),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "subjects" && (
        <SetupCard title="Subjects">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(event, "subject", "academic/subjects/", "Subject saved.")
            }
          >
            <TextField label="Subject Code" value={forms.subject.code} onChange={(value) => updateForm("subject", "code", value.toUpperCase())} placeholder="e.g. MATH" />
            <TextField label="Subject Name" value={forms.subject.name} onChange={(value) => updateForm("subject", "name", value)} placeholder="e.g. Mathematics" />
            <div className="col-md-4">
              <label className="form-label fw-semibold">Subject Type</label>
              <select
                className="form-select"
                value={forms.subject.subject_type}
                onChange={(event) => updateForm("subject", "subject_type", event.target.value)}
              >
                <option value="CLASS_TEACHER">Class Teacher Subject</option>
                <option value="SPECIALIST">Specialist Subject</option>
              </select>
            </div>
            <TextField label="Description" value={forms.subject.description} onChange={(value) => updateForm("subject", "description", value)} optional />
            <div className="col-md-2">{renderBooleanInput("subject", "is_core", "Core")}</div>
            <div className="col-md-2">{renderBooleanInput("subject", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Subject"
              editing={Boolean(editing.subject)}
              onCancel={() => cancelEdit("subject")}
            />
          </form>

          <SimpleTable
            headers={["Code", "Subject", "Type", "Core", "Status", "Actions"]}
            rows={subjects.map((subject) => [
              subject.code,
              subject.name,
              subject.subject_type_label || subject.subject_type || "Class Teacher Subject",
              subject.is_core ? "Yes" : "No",
              renderStatus(subject.is_active),
              renderActions(
                "subject",
                subject,
                `academic/subjects/${subject.id}/`,
                subject.name
              ),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "classSubjects" && (
        <SetupCard title="Class Subjects">
          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(
                event,
                "classSubject",
                "academic/class-subjects/",
                "Class subject saved."
              )
            }
          >
            <SelectField label="Class Group" value={forms.classSubject.class_group} onChange={(value) => updateForm("classSubject", "class_group", value)} options={groups.map((group) => ({ value: group.id, label: `${group.academic_year_name} - ${group.display_name}` }))} />
            <SelectField label="Subject" value={forms.classSubject.subject} onChange={(value) => updateForm("classSubject", "subject", value)} options={subjects.map((subject) => ({ value: subject.id, label: `${subject.code} - ${subject.name}` }))} />
            <div className="col-md-2">{renderBooleanInput("classSubject", "is_compulsory", "Compulsory")}</div>
            <div className="col-md-2">{renderBooleanInput("classSubject", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Class Subject"
              editing={Boolean(editing.classSubject)}
              onCancel={() => cancelEdit("classSubject")}
            />
          </form>

          <SimpleTable
            headers={["Year", "Class", "Subject", "Type", "Compulsory", "Status", "Actions"]}
            rows={classSubjects.map((item) => [
              item.academic_year_name,
              item.class_group_name,
              `${item.subject_code} - ${item.subject_name}`,
              getSubjectTypeBadge(item.subject_type, item.subject_type_label),
              item.is_compulsory ? "Yes" : "No",
              renderStatus(item.is_active),
              renderActions(
                "classSubject",
                item,
                `academic/class-subjects/${item.id}/`,
                item.subject_name
              ),
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "allocations" && (
        <SetupCard title="Teacher Allocations">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <div className="border rounded-3 p-3 bg-light">
                <small className="text-muted fw-bold">Active Specialist Allocations</small>
                <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{specialistAllocationCount}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="border rounded-3 p-3 bg-light">
                <small className="text-muted fw-bold">Active Class Teacher Subject Allocations</small>
                <div className="fs-5 fw-bold" style={{ color: "#125b2d" }}>{classTeacherAllocationCount}</div>
              </div>
            </div>
          </div>

          <form
            className="row g-3 mb-4"
            onSubmit={(event) =>
              submitForm(
                event,
                "allocation",
                "academic/teacher-allocations/",
                "Teacher allocation saved."
              )
            }
          >
            <SelectField label="Teacher" value={forms.allocation.teacher} onChange={(value) => updateForm("allocation", "teacher", value)} options={teachers.map((teacher) => ({ value: teacher.id, label: `${teacher.full_name} (${teacher.employee_number || "No employee no."})` }))} />
            <div className="col-12">
              <div className="alert alert-light border mb-0">
                <div className="fw-bold mb-1">
                  <i className="bi bi-shield-check me-2 text-success"></i>
                  Specialist teacher rule
                </div>
                <small className="text-muted">
                  Specialist subjects must be assigned here first. Timetable, e-learning, and exams can then reuse this allocation professionally.
                </small>
              </div>
            </div>
            <SelectField label="Class Subject" value={forms.allocation.class_subject} onChange={(value) => updateForm("allocation", "class_subject", value)} options={classSubjectOptions} />
            <SelectField label="Term" value={forms.allocation.term} onChange={(value) => updateForm("allocation", "term", value)} options={terms.map((term) => ({ value: term.id, label: `${term.academic_year_name} - ${term.name}` }))} />
            <div className="col-md-2">{renderBooleanInput("allocation", "is_lead_teacher", "Lead")}</div>
            <div className="col-md-2">{renderBooleanInput("allocation", "is_active", "Active")}</div>
            <SubmitButton
              loading={loading}
              label="Save Allocation"
              editing={Boolean(editing.allocation)}
              onCancel={() => cancelEdit("allocation")}
            />
          </form>

          <SimpleTable
            headers={["Teacher", "Class", "Subject", "Type", "Term", "Lead", "Status", "Actions"]}
            rows={allocations.map((item) => [
              item.teacher_name,
              item.class_group_name,
              item.subject_name,
              getSubjectTypeBadge(item.subject_type, item.subject_type_label),
              `${item.academic_year_name} - ${item.term_name}`,
              item.is_lead_teacher ? "Yes" : "No",
              renderStatus(item.is_active),
              renderActions(
                "allocation",
                item,
                `academic/teacher-allocations/${item.id}/`,
                item.teacher_name
              ),
            ])}
          />
        </SetupCard>
      )}
    </div>
  );
}

function SetupCard({ title, children }) {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white">
        <h6 className="fw-bold mb-0">{title}</h6>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function SubmitButton({ loading, label, editing = false, onCancel }) {
  return (
    <div className="col-12 d-flex flex-wrap gap-2">
      <button className="btn btn-success fw-semibold" disabled={loading}>
        {loading ? "Saving..." : editing ? "Update Record" : label}
      </button>

      {editing && (
        <button
          type="button"
          className="btn btn-light border fw-semibold"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel Edit
        </button>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder = "", optional = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <input
        className="form-control"
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={!optional}
      />
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div className="col-md-2">
      <label className="form-label fw-semibold">{label}</label>
      <input
        type="number"
        className="form-control"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        min="1"
        required
      />
    </div>
  );
}

function DateField({ label, value, onChange }) {
  return (
    <div className="col-md-3">
      <label className="form-label fw-semibold">{label}</label>
      <input
        type="date"
        className="form-control"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, optional = false }) {
  return (
    <div className="col-md-4">
      <label className="form-label fw-semibold">{label}</label>
      <select
        className="form-select"
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        required={!optional}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function SimpleTable({ headers, rows }) {
  return (
    <div className="table-responsive">
      <table className="table table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center text-muted py-4">
                No records yet.
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr key={index}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex}>{cell}</td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default AcademicSetup;
